import React, { useState, useEffect } from "react";
import axios from "axios";
import { Minus, Plus, Trash2, ShoppingBag, Truck, Loader2, Lock, Tag, Check, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { formatPrice } from "../mock";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHIPPING_FALLBACK = [
  { id: "dhl", label: "DHL", price: 0 },
  { id: "hermes", label: "Hermes", price: 0 },
  { id: "dpd", label: "DPD", price: 0 },
];

const calcDiscount = (discount, total) => {
  if (!discount) return 0;
  if (discount.type === "percent") return Math.min(Math.round(total * discount.value) / 100, total);
  return Math.min(discount.value, total);
};

const CartDrawer = () => {
  const { items, open, setOpen, setQty, remove, total } = useCart();
  const { user } = useAuth();
  const [shippingOptions, setShippingOptions] = useState(SHIPPING_FALLBACK);
  const [shipping, setShipping] = useState("dhl");
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(null); // {code, type, value, label}
  const [checkingCode, setCheckingCode] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/shipping`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length) setShippingOptions(res.data);
      })
      .catch(() => {});
  }, []);

  const shippingCost = shippingOptions.find((s) => s.id === shipping)?.price ?? 0;
  const discountAmount = calcDiscount(discount, total);
  const grandTotal = Math.max(0, total - discountAmount) + shippingCost;

  const applyCode = async () => {
    if (!code.trim()) return;
    setCheckingCode(true);
    try {
      const res = await axios.post(`${API}/discount/validate`, { code: code.trim() });
      setDiscount(res.data);
      toast.success(`Gutschein „${res.data.code}" angewendet: ${res.data.label}`);
    } catch {
      setDiscount(null);
      toast.error("Ungültiger oder abgelaufener Gutscheincode.");
    } finally {
      setCheckingCode(false);
    }
  };

  const removeCode = () => {
    setDiscount(null);
    setCode("");
  };

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        items: items.map((i) => ({ id: i.id, qty: i.qty })),
        shipping,
        origin_url: window.location.origin,
        user_id: user?.user_id || null,
        discount_code: discount?.code || null,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        throw new Error("no url");
      }
    } catch (e) {
      setLoading(false);
      toast.error("Checkout konnte nicht gestartet werden. Bitte versuche es erneut.");
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl">
            <span className="text-[#1b7fc1]">Dein</span>{" "}
            <span className="text-[#6fbf3f]">Warenkorb</span>
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center py-16">
            <div className="p-6 rounded-full bg-sky-50">
              <ShoppingBag className="h-10 w-10 text-[#1b9ad6]" />
            </div>
            <p className="font-bold text-slate-700 text-lg">Dein Warenkorb ist leer</p>
            <p className="text-slate-500 text-sm">Entdecke unsere fertigen 3D-Produkte!</p>
            <button onClick={() => setOpen(false)} className="btn-gradient mt-2">
              Weiter stöbern
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto py-4 space-y-4 pr-1">
              {items.map((item) => (
                <div key={item.id} className="flex gap-3 rounded-2xl border border-slate-100 p-3 bg-white shadow-sm">
                  <img src={item.image} alt={item.name} className="h-20 w-20 rounded-xl object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-800 truncate">
                      {item.name} <span className="text-slate-400 font-semibold">– {item.variant}</span>
                    </p>
                    <p className="text-[#f7941e] font-extrabold">{formatPrice(item.price)}</p>
                    <div className="mt-1 flex items-center gap-2">
                      <button
                        aria-label="Menge verringern"
                        onClick={() => setQty(item.id, item.qty - 1)}
                        className="h-7 w-7 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-sky-50 hover:border-[#1b9ad6] transition-colors"
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span className="w-6 text-center font-bold text-slate-700">{item.qty}</span>
                      <button
                        aria-label="Menge erhöhen"
                        onClick={() => setQty(item.id, item.qty + 1)}
                        className="h-7 w-7 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-sky-50 hover:border-[#1b9ad6] transition-colors"
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <button
                    aria-label="Entfernen"
                    onClick={() => remove(item.id)}
                    className="self-start p-2 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}

              {/* Versandauswahl */}
              <div className="rounded-2xl border border-slate-100 p-4">
                <p className="flex items-center gap-2 font-bold text-slate-700 mb-3">
                  <Truck className="h-4.5 w-4.5 text-[#1b9ad6]" /> Versand
                </p>
                <div className="space-y-2">
                  {shippingOptions.map((opt) => (
                    <label
                      key={opt.id}
                      className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${
                        shipping === opt.id
                          ? "border-[#1b9ad6] bg-sky-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <span className="flex items-center gap-2.5">
                        <input
                          type="radio"
                          name="shipping"
                          value={opt.id}
                          checked={shipping === opt.id}
                          onChange={() => setShipping(opt.id)}
                          className="accent-[#1b9ad6] h-4 w-4"
                        />
                        <Truck className="h-4 w-4 text-slate-500" />
                        <span className="font-semibold text-slate-700">{opt.label}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4 space-y-2">
              {/* Gutscheincode */}
              <div className="pb-2">
                {discount ? (
                  <div className="flex items-center justify-between gap-2 rounded-xl bg-lime-50 border border-lime-200 px-3 py-2.5">
                    <span className="flex items-center gap-2 font-bold text-[#3f8f2a] text-sm">
                      <Check className="h-4 w-4" /> {discount.code} · {discount.label}
                    </span>
                    <button
                      onClick={removeCode}
                      aria-label="Gutschein entfernen"
                      className="p-1 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        onKeyDown={(e) => e.key === "Enter" && applyCode()}
                        placeholder="Gutscheincode"
                        className="input-soft !rounded-full !pl-9 h-11 w-full"
                      />
                    </div>
                    <button
                      onClick={applyCode}
                      disabled={checkingCode || !code.trim()}
                      className="h-11 px-5 rounded-full font-bold text-white bg-gradient-to-r from-[#1b9ad6] to-[#1273a8] disabled:opacity-50 transition-opacity"
                    >
                      {checkingCode ? <Loader2 className="h-4 w-4 animate-spin" /> : "Einlösen"}
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between text-sm text-slate-600">
                <span>Zwischensumme</span>
                <span className="font-semibold">{formatPrice(total)}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex items-center justify-between text-sm text-[#3f8f2a]">
                  <span>Rabatt ({discount?.code})</span>
                  <span className="font-semibold">−{formatPrice(discountAmount)}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-1">
                <span className="font-semibold text-slate-700">Gesamt</span>
                <span className="font-display text-2xl font-extrabold text-slate-900">
                  {formatPrice(grandTotal)}
                </span>
              </div>
              <button onClick={checkout} disabled={loading} className="btn-gradient-warm w-full disabled:opacity-60">
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" /> Weiterleitung ...
                  </>
                ) : (
                  <>
                    <Lock className="h-5 w-5" /> Sicher bezahlen
                  </>
                )}
              </button>
              <p className="text-center text-xs text-slate-400 pt-1">
                Sichere Zahlung über Stripe
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
